import type { VisualRole } from "@eldrun/content";
import { Color, ShaderMaterial, Vector2, Vector4, type IUniform } from "three";
import { applyRole, roleUniforms } from "../style/roles.ts";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { assemble } from "./chunks.ts";
import { NEAR_FADE, NEAR_FADE_LEAF_WIND } from "./near-fade.ts";

/** Quanto da normal de moita entra na luz da copa (Fase 14): 0 facetada, 1 bola lisa. */
export const CROWN_ROUND = 0.6;

/**
 * Vértice do material de superfície: `transformed` (a posição; a troca de LOD é no fragmento) → skinning
 * (`SkinnedMesh`, trechos do three) → `instanceMatrix` (lotes, F36) → `modelMatrix` → vento. A cor
 * por vértice (com paletas e a cor da instância) só existe com cor por vértice ou por instância.
 */
export const SURFACE_VERTEX = /* glsl */ `
${assemble("vertex", ["atmo", "wind", "farFade", "nearFade", "palettes", "skin", "style"])}
#ifdef USE_WIND
in float wind;
uniform float uWindStrength;
// Fase 14: a normal de moita da copa (0 fora dela; \`crownNormals\`), no mundo.
in vec3 aCrown;
out vec3 vCrown;
#endif
out vec3 vWorldPos;
#if defined(USE_COLOR) || defined(USE_INSTANCING_COLOR)
out vec3 vColor;
#endif

void main() {
  mat4 instance = mat4(1.0);
  #ifdef USE_INSTANCING
    instance = instanceMatrix;
  #endif
  #ifdef USE_FAR_FADE
    float lodDistance = distance((modelMatrix * instance[3]).xz, cameraPosition.xz);
    vLodFade = vec2(smoothstep(uFarFade.x, uFarFade.y, lodDistance), 1.0 - smoothstep(uFarFade.z, uFarFade.w, lodDistance));
  #endif
  vec3 transformed = position;
  #include <skinbase_vertex>
  #include <skinning_vertex>
  vec4 world = modelMatrix * (instance * vec4(transformed, 1.0));
  #ifdef USE_WIND
    world.xz += foliageWind(instance[3].xz, wind, uWindStrength, 1.0);
  #endif
  vWorldPos = world.xyz;
  #ifdef USE_WIND
    vCrown = wind >= 0.3 ? mat3(modelMatrix) * mat3(instance) * aCrown : vec3(0.0);
  #endif
  #if defined(USE_COLOR) || defined(USE_INSTANCING_COLOR)
    vColor = vec3(1.0);
    #ifdef USE_COLOR
      vColor = color;
      #ifdef USE_PALETTES
        vColor = paletteColor(color);
      #endif
    #endif
    #ifdef USE_INSTANCING_COLOR
      vColor *= instanceColor;
    #endif
  #endif
  gl_Position = projectionMatrix * viewMatrix * world;
  #ifdef USE_FAR_FADE
    // Fora da faixa do seu LOD a instância não tem pixel: o vértice sai do recorte (nada rasteriza).
    if (vLodFade.x <= 0.0 || vLodFade.y <= 0.0) gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
  #endif
  styleVertex();
  #ifdef USE_NEAR_FADE
    vViewDepth = -(viewMatrix * world).z;
  #endif
  #ifdef USE_NEAR_FADE_FLOOR
    // Base da instância no mundo (a translação dela); o vento só mexe em x e z.
    vBaseHeight = world.y - (modelMatrix * instance[3]).y;
    #ifdef USE_WIND
      vLeaf = step(${NEAR_FADE_LEAF_WIND.toFixed(2)}, wind);
    #else
      vLeaf = 0.0;
    #endif
  #endif
}
`;

/**
 * Fragmento: todas as derivadas primeiro (normal por face e largura da rampa) → luz (rampa, sol,
 * preenchimento, rim; o brilho próprio, que no Alto passa de 1, F52) → sombras-blob → neblina → todo
 * descarte (sumiço perto, troca de LOD, dissolve) → alfa (a máscara do contorno). Com vizinhos descartados em divergência, uma derivada depois do descarte
 * fica indefinida e a luz pisca.
 */
export const SURFACE_FRAGMENT = /* glsl */ `
${assemble("fragment", ["atmo", "keyFill", "rimNight", "blobFragment", "fogGlow", "nearFade", "farFade", "dissolve", "outline", "glow", "style", "energy", "sunShadow"])}
uniform vec3 uColor;
uniform float uGlow;
in vec3 vWorldPos;
#ifdef USE_WIND
in vec3 vCrown;
#endif
#ifdef USE_COLOR
in vec3 vColor;
#endif

void main() {
  vec3 normal = normalize(cross(dFdx(vWorldPos), dFdy(vWorldPos)));
  vec3 view = normalize(cameraPosition - vWorldPos);
  #ifdef NORMAL_UP
    if (normal.y < 0.0) normal = -normal;
  #else
    if (dot(normal, view) < 0.0) normal = -normal;
  #endif
  #ifdef USE_WIND
    // Copa redonda (Fase 14, a referência): a luz pela normal da moita, misturada à da face (as facetas
    // ainda leem). Fora da copa, vCrown é 0 e a face fica.
    if (dot(vCrown, vCrown) > 0.01) normal = normalize(mix(normal, normalize(vCrown), ${CROWN_ROUND.toFixed(2)}));
  #endif
  float ndl = dot(normal, uLightDir);
  float w = rampWidth(ndl);

  vec3 albedo = uColor;
  float glow = uGlow;
  #ifdef USE_COLOR
    albedo = vColor * uColor;
    // F52: no Alto, o metal de pedra-de-raio brilha.
    glow = max(glow, glowKeyed(vColor));
  #endif
  vec3 ambient = toonAmbient(normal, ndl);
  // Brilho próprio (olhos, fogo): a cor não depende da luz, só a neblina age; no Alto, acima de 1 (F52).
  // Sombra do sol (G3) só no sol: o ambiente fica.
  vec3 color = glow > 0.5 ? albedo * glowBoost(albedo) : albedo * (ambient + toonKey(ndl, w) * sunShadow(vWorldPos, normal));
  // O rim entra proporcional ao albedo (normalizado pela luminância): o styleCompose extrai a cor
  // da luz dividindo por canal pelo albedo, e um termo aditivo puro voltaria com o matiz do
  // material INVERTIDO (pele quente virava rim azulado; revisão da Fase 12).
  color += rimLight(normal, view) * albedo / max(dot(albedo, STYLE_LUMA), 1e-3);
  #ifdef USE_BLOBS
    color = blobShade(color, albedo * ambient, blobShadow(vWorldPos));
  #endif
  color = styleOut(color, albedo, normal, view);
  // Resposta local de combate (pesquisa 11): só o AMBIENTE (papéis sem acento nem rim: árvores,
  // rochas, estruturas) ganha um banho fraco na cor do dono. Atores
  // ficam de fora: a cor de classe, o laranja e o vermelho são linguagem de gameplay (ADR 0017) e
  // um banho ciano sobre um monstro laranja a quebraria.
  // Desvio por uniforme (o mesmo para o draw inteiro): os atores nem rodam o laço dos eventos (Gate B4).
  if (uStyleRole.w + uStyleAccent.w < 0.001) color += energyGlow(vWorldPos) * 0.12;
  #ifdef USE_DISSOLVE
    float grain = dissolveGrain(vWorldPos);
    color = dissolveEdge(color, grain);
  #endif
  color = applyFog(color, vWorldPos, cameraPosition);

  #ifdef USE_NEAR_FADE
    nearFade();
  #endif
  #ifdef USE_FAR_FADE
    farFade();
  #endif
  #ifdef USE_DISSOLVE
    if (grain < abs(uDissolve)) discard;
  #endif
  gl_FragColor = vec4(color, outlineAlpha());
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export interface SurfaceOptions {
  atmosphere: AtmosphereUniforms;
  name: string;
  /** Cor sólida (`uColor`); com cor por vértice, multiplica a do vértice (branco por padrão). */
  color?: string;
  vertexColors?: boolean;
  /** Paletas por instância (`USE_PALETTES`, F42). */
  palettes?: boolean;
  /** Sumiço perto da câmera (`USE_NEAR_FADE`); `floor`, o piso da casca nos recursos. */
  nearFade?: { floor?: number };
  /** Sumiço na distância (`USE_FAR_FADE`): início e fim (m) da saída e, opcional, da entrada (o LOD de longe). */
  farFade?: [number, number, ([number, number] | undefined)?];
  /** Vento por vértice (`USE_WIND`, atributo `wind`) com esta força. */
  wind?: number;
  /** Contorno de luz (0 sem). */
  rim?: number;
  /** Brilho próprio: só a neblina age. */
  glow?: boolean;
  /** Rim frio noturno (só as assombrações). */
  nightRim?: boolean;
  /** Dissolve (`USE_DISSOLVE`). */
  dissolve?: boolean;
  /** Recebe as sombras-blob (`USE_BLOBS`). */
  blobs?: boolean;
  /** Normal por face virada para a câmera (padrão) ou para cima (`NORMAL_UP`, chão). */
  normal?: "camera" | "up";
  /** Máscara do contorno (alfa 0): personagens, itens e estruturas. */
  outline?: boolean;
  /** Papel visual na malha neon (F59; padrão `estrutura`) e a cor do dono (padrão, o neutro). */
  role?: VisualRole;
  accent?: string;
}

/** Paletas: atributo ausente lê o último valor genérico da posição (de outro programa); fixa os padrões. */
export const PALETTE_DEFAULTS = { color1: [1, 1, 1], color2: [1, 1, 1], palette: [0] };

/** Máscara de acento (F58) e normal de moita (Fase 14): sem os atributos, nada acende e a face fica. */
export const STYLE_DEFAULTS = { tint: [0], aCrown: [0, 0, 0] };

/**
 * A chave do programa é só o conjunto de defines: a fonte é a mesma em todo material de superfície,
 * e cor, rim, brilho e contorno são uniforms (nenhum programa novo por cor ou por classe).
 */
function keyByDefines(material: ShaderMaterial): ShaderMaterial {
  material.customProgramCacheKey = () => Object.keys(material.defines ?? {}).sort().join("|");
  return material;
}

/**
 * Material de superfície (F51): toon facetado com a rampa de 3 faixas, sol quente e preenchimento
 * frio, rim (e o noturno), névoa com o brilho do sol, vento, sumiços, paletas, sombras-blob,
 * dissolve, skinning e a máscara do contorno, numa fonte só. Os opcionais são defines; o resto,
 * uniforms. Divide os uniforms da atmosfera por referência.
 */
export function createSurfaceMaterial(o: SurfaceOptions): ShaderMaterial {
  const defines: Record<string, string> = {};
  const uniforms: Record<string, IUniform> = {
    ...o.atmosphere,
    uColor: { value: new Color(o.color ?? "#ffffff") },
    uRim: { value: o.rim ?? 0 },
    uGlow: { value: o.glow ? 1 : 0 },
    uRimNightOn: { value: o.nightRim ? 1 : 0 },
    uOutline: { value: o.outline ? 1 : 0 },
    // Malha neon (F58/F59): o papel visual e a cor do dono (`render/style/roles.ts`).
    ...roleUniforms(o.role ?? "estrutura", o.accent),
  };
  if (o.wind !== undefined) {
    defines["USE_WIND"] = "";
    uniforms["uWindStrength"] = { value: o.wind };
  }
  if (o.palettes) defines["USE_PALETTES"] = "";
  if (o.nearFade) {
    defines["USE_NEAR_FADE"] = "";
    uniforms["uNearFade"] = { value: new Vector2(NEAR_FADE.gone, NEAR_FADE.solid) };
    if (o.nearFade.floor !== undefined) {
      defines["USE_NEAR_FADE_FLOOR"] = "";
      uniforms["uNearFadeFloor"] = { value: o.nearFade.floor };
    }
  }
  if (o.farFade) {
    defines["USE_FAR_FADE"] = "";
    uniforms["uFarFade"] = { value: lodFadeBand(o.farFade[0], o.farFade[1], o.farFade[2]) };
  }
  if (o.dissolve) {
    defines["USE_DISSOLVE"] = "";
    uniforms["uDissolve"] = { value: 0 };
    // A borda na cor do acento (F95: o mesmo uniform do estilo).
    uniforms["uDissolveEdge"] = uniforms["uStyleAccent"]!;
  }
  if (o.blobs) defines["USE_BLOBS"] = "";
  if (o.normal === "up") defines["NORMAL_UP"] = "";
  const material = new ShaderMaterial({ name: o.name, uniforms, vertexShader: SURFACE_VERTEX, fragmentShader: SURFACE_FRAGMENT, vertexColors: o.vertexColors === true, defines });
  Object.assign(material.defaultAttributeValues, STYLE_DEFAULTS);
  if (o.palettes) Object.assign(material.defaultAttributeValues, PALETTE_DEFAULTS);
  return keyByDefines(material);
}

/**
 * Variante de um material de superfície: a mesma fonte com defines a mais. `uniforms` entram sobre
 * uma cópia rasa dos do original (o resto continua dividido por referência); com `shared`, a variante
 * usa o próprio objeto de uniforms do original.
 */
/**
 * `uFarFade` (`USE_FAR_FADE`): entra entre `fadeIn` (sem ela, já inteiro a 0 m) e sai entre `start` e `end` (m).
 */
export function lodFadeBand(start: number, end: number, fadeIn?: readonly [number, number]): Vector4 {
  return new Vector4(fadeIn?.[0] ?? -2, fadeIn?.[1] ?? -1, start, end);
}

export function surfaceVariant(material: ShaderMaterial, variant: { name: string; defines?: Record<string, string>; uniforms?: Record<string, IUniform>; shared?: boolean }): ShaderMaterial {
  const out = new ShaderMaterial({
    name: variant.name,
    uniforms: variant.shared ? material.uniforms : { ...material.uniforms, ...variant.uniforms },
    vertexShader: material.vertexShader,
    fragmentShader: material.fragmentShader,
    vertexColors: material.vertexColors,
    defines: { ...material.defines, ...variant.defines },
  });
  Object.assign(out.defaultAttributeValues, material.defaultAttributeValues);
  return keyByDefines(out);
}

/**
 * Variante com outro papel visual (F59): a mesma fonte e os mesmos defines (nenhum programa novo), com
 * uniforms de estilo próprios. As variantes por `surfaceVariant` dividem os do original por referência;
 * esta não, então mudar o papel dela não muda o do original.
 */
export function withRole(material: ShaderMaterial, role: VisualRole, accent?: string): ShaderMaterial {
  const out = surfaceVariant(material, { name: `${material.name}-${role}`, uniforms: { ...roleUniforms(role, accent) } });
  applyRole(out, role, accent);
  return out;
}
